import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, TextField, FormControl } from '@mui/material';
import { ModalV2 } from 'components/Modal/ModalV2';
import RegularButton from 'components/CustomButtons/Button';
import { SelectComponent } from 'components/SelectMenu/SelectComponent';
import { useForm } from 'hooks/useForm';
import { useSearch } from 'hooks/useSearch';
import { getOrderCauses, newPaymentLink, editOrder } from 'redux/actions/orders/ordersActions';
import { cleanNumber } from 'helpers/cleanNumbers';
import { loadStationConfig } from 'redux/actions/configSync/configSyncAction';
import { statusOrder } from 'helpers/statusVariable';

const initialValues = {
  action: '',
  cause: '',
};

const validations = {
  action: { required: true },
  cause: { required: false },
};

export const SubscriptionApprovalModal = ({ open, onClose, order, onSuccess }) => {
  const dispatch = useDispatch();
  const { setSnackbar } = useSearch();
  const { causes } = useSelector(({ orders }) => orders);
  const { stationConfig } = useSelector(({ configSync }) => configSync || {});
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { inputValues, handleInputOnChange, fieldErrors, cleanForm } = useForm(
    initialValues,
    validations
  );

  const { action, cause } = inputValues;

  const actionOptions = [
    { value: 'APPROVE', label: 'Aceptar' },
    { value: 'REJECT', label: 'Rechazar' },
  ];

  useEffect(() => {
    if (open && order?.idStation) {
      dispatch(getOrderCauses(order.idStation));
      if (order?.serviceComplement?.idBrand) {
        dispatch(loadStationConfig(order.idStation, 'PAYMENT', order.serviceComplement.idBrand));
      }
    }
  }, [open, order?.idStation, order?.serviceComplement?.idBrand]);

  useEffect(() => {
    if (stationConfig?.OPENPAY) {
      setPaymentConfig(stationConfig.OPENPAY);
    } else {
      setPaymentConfig(null);
    }
  }, [stationConfig]);

  const generateOpenPaySessionId = () => {
    if (!paymentConfig) return null;
    if (!window.OpenPay) {
      setSnackbar('OpenPay no disponible', 'danger');
      return null;
    }
    window.OpenPay.setSandboxMode(process.env.REACT_APP_NODE_ENV !== 'PROD');
    window.OpenPay.setId(paymentConfig.merchant_id ?? paymentConfig.API_KEY);
    window.OpenPay.setApiKey(paymentConfig.public_key ?? paymentConfig.API_KEY);
    return window.OpenPay.deviceData.setup();
  };

  const handleSubmit = async () => {
    if (!action) {
      setSnackbar('Seleccione una acción', 'warning');
      return;
    }

    if (action === 'REJECT' && !cause) {
      setSnackbar('Seleccione un motivo de cancelación', 'warning');
      return;
    }

    setIsProcessing(true);

    if (action === 'APPROVE') {
      const paymentType = order?.serviceComplement?.paymentType || order?.paymentType;
      const isPaymentLink = paymentType?.toLowerCase() === 'payment_link';
      const needsCardPayment = ['card', 'credit_card', 'debit_card'].some(type => 
        type === paymentType?.toLowerCase()
      );

      if (needsCardPayment || isPaymentLink) {
        // Validar si ya existe un pago
        if (order?.payments && order.payments.length > 0) {
          setSnackbar('Este pedido ya tiene un pago registrado', 'warning');
          setIsProcessing(false);
          return;
        }

        if (!paymentConfig) {
          setSnackbar('Configuración de pago no disponible', 'danger');
          setIsProcessing(false);
          return;
        }

        const totalNum = cleanNumber(order?.total);
        let sessionId = null;
        
        try {
          sessionId = generateOpenPaySessionId();
        } catch (error) {
          setSnackbar('Error al generar la sesión de pago', 'danger');
        }

        const paymentData = {
          id_customer: order?.idCustomer,
          amount: totalNum,
          description: `Pago de servicio ${order?.id}`,
          currency: 'MXN',
          origin: 'SERVICES',
          id_order: order?.id,
          id_brand: order?.serviceComplement?.idBrand,
          id_station: order?.idStation,
          type: isPaymentLink ? 'PAYMENT_LINK' : 'CARD',
          processor: 'OPENPAY',
          capture: false,
          device_session_id: sessionId,
          use_3d_secure: true,
        };

        // Agregar idCard solo si es pago con tarjeta guardada
        if (!isPaymentLink && order?.serviceComplement?.idCard) {
          paymentData.id_card = order.serviceComplement.idCard;
        }

        const approvalForm = {
          status: order?.status,
          subscriptionApproved: true,
        };

        try {
          await dispatch(newPaymentLink(paymentData));
          await dispatch(editOrder(order?.id, approvalForm, null));
          setSnackbar('Pago generado y pedido aceptado exitosamente', 'success');
          cleanForm();
          if (onSuccess) onSuccess();
          onClose();
        } catch (error) {
          setSnackbar('Error al procesar el pago o actualizar el pedido', 'danger');
        } finally {
          setIsProcessing(false);
        }
      } else {
        // Para efectivo, solo aprobar suscripción
        const approvalForm = {
          status: order?.status,
          subscriptionApproved: true,
        };
        
        try {
          await dispatch(editOrder(order?.id, approvalForm, null));
          setSnackbar('Pedido aceptado exitosamente', 'success');
          cleanForm();
          if (onSuccess) onSuccess();
          onClose();
        } catch (error) {
          setSnackbar('Error al aprobar el pedido', 'danger');
        } finally {
          setIsProcessing(false);
        }
      }
    } else {
      // Rechazar pedido
      const form = {
        cause,
        status: statusOrder.CANCELED.status,
        subscriptionApproved: false,
      };

      try {
        await dispatch(editOrder(order?.id, form, null));
        setSnackbar('Pedido rechazado', 'success');
        cleanForm();
        if (onSuccess) onSuccess();
        onClose();
      } catch (error) {
        setSnackbar('Error al rechazar el pedido', 'danger');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      cleanForm();
      onClose();
    }
  };

  return (
    <ModalV2 open={open} onClose={handleClose} title="Aprobar/Rechazar Pedido" toRight>
      <FormControl fullWidth>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Cliente"
              value={order?.customer || ''}
              variant="filled"
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Cuenta"
              value={order?.account || ''}
              variant="filled"
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Monto"
              value={order?.total || ''}
              variant="filled"
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <SelectComponent
              label="Acción"
              name="action"
              options={actionOptions}
              value={action}
              onChange={handleInputOnChange}
              error={fieldErrors.action}
              fullWidth
            />
          </Grid>
          {action === 'REJECT' && (
            <Grid item xs={12}>
              <SelectComponent
                label="Motivo de cancelación"
                name="cause"
                options={causes}
                value={cause}
                onChange={handleInputOnChange}
                error={fieldErrors.cause}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <RegularButton
              color="info"
              onClick={handleSubmit}
              fullWidth
              disabled={isProcessing}
            >
              {isProcessing ? 'Procesando...' : 'Confirmar'}
            </RegularButton>
          </Grid>
        </Grid>
      </FormControl>
    </ModalV2>
  );
};
